// ✅ DROPDOWN_OPTIONS.js
// Centralized dropdown options for Products, Grades, Brands, and Pack Sizes
// Now loads dynamically from database using dropdowns.php API

import axios from 'axios';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ccc4cs.choicegroup.in/api';

// ✅ BLOCKED PACK SIZES - These will be filtered out everywhere
const BLOCKED_PACK_SIZES = ['10/32 OZ', '10X32 OZ', '10/32OZ'];

// ✅ Default fallback values (used before DB loads)
let PRODUCTS = [
  'CBKN', 'CEZP', 'CEZPNC', 'CIST', 'CIST EZP CUT', 'CIST EZP CUT-TG',
  'CISTSR', 'COOKED', 'CPND', 'CPND NC', 'CPND-BKN', 'CPND-NP',
  'CPTO', 'CPTO-NP', 'HLSO', 'HLSO-BT', 'RAW', 'RBKN', 'REZP',
  'REZPNC', 'REZPNC-TG', 'RHLW', 'RPND', 'RPND-BKN', 'RPND-NP',
  'RPTO', 'RPVPD', 'RTNC', 'RTNC-TG'
];

let BRANDS = [
  'BOWL AND BASKET', 'BLACK TAIL', 'BROKEN', 'DECOLOUR', 'FALLEN',
  'FINAL BAG BULK', 'FREMONT FISH MARKET', 'GOLD STAR', 'GREAT CATCH',
  'HARRIS TEETER  FM', 'INSPECTION', 'MARKET BASKET', 'MIX',
  'NATURES PROMISE', 'PLAIN BAG BULK', 'PLAIN-HARRIS TEETER', 'REJECTION',
  'SEAQUEEN', 'SISTER SIZE', 'SPECIALLY SELECTED', 'SOUTHERN SEAS',
  'TC', 'TC HARRIS TEETER', 'TC HEB', 'WHOLESOME PANTRY'
];

let GRADES = [
  '6/8', '8/12', '13/15', '16/20', '16/25', '20/40', '21/25', '26/30',
  '31/40', '40/60', '41/50', '41/60', '51/60', '60/80', '61/70', '71/90',
  '80/100', '91/110', '91/120', '100/150', '110/150', 'ALL MIX', 'BROKEN', 'FALLEN', 'INS'
];

// ✅ REMOVED: '10/32 OZ' from this list
let PACK_SIZES = [
  '10/1 LB', '10/16 OZ', '10/2 LB', '12/1 LB', '12/12 OZ',
  '12/16 OZ', '15/1 LB', '15/2 LB', '16/1 LB', '16/12 OZ', '16/16 OZ',
  '1/10 KG', '1/22 LB', '20/1 LB', '20/12 OZ', '20/16 OZ', '24/12 OZ',
  '3/7 LB', '4/5 LB', '5/2 LB', '5/4 LB', '6/4 LB', '6/5 LB', 'LOOSE KG'
];

export const PACK_STYLES = ['FINAL', 'FBB', 'PBB', 'BULK'];

// Export mutable arrays
export { PRODUCTS, GRADES, BRANDS, PACK_SIZES };

// Get auth config
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true
  };
};

// ✅ Helper function to filter out blocked pack sizes
const filterBlockedPackSizes = (packSizes) => {
  if (!packSizes || !Array.isArray(packSizes)) return [];
  return packSizes.filter(ps => {
    const normalized = String(ps).toUpperCase().trim().replace(/X/g, '/');
    return !BLOCKED_PACK_SIZES.some(blocked => 
      normalized === blocked.toUpperCase().replace(/X/g, '/')
    );
  });
};

// ============================================
// ✅ GENERIC FUNCTION TO LOAD FROM DATABASE
// ============================================
const loadFromDatabase = async (type, targetArray) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/dropdowns.php?type=${type}`,
      getAuthConfig()
    );
    
    if (response.data.success && response.data.data && response.data.data.length > 0) {
      let data = response.data.data;
      
      // ✅ Filter out blocked pack sizes
      if (type === 'pack_sizes') {
        data = filterBlockedPackSizes(data);
      }
      
      targetArray.length = 0; // Clear array
      targetArray.push(...data);
      console.log(`✅ ${type} loaded from DB:`, targetArray.length);
      return targetArray;
    }
  } catch (error) {
    console.error(`Failed to load ${type} from DB:`, error);
  }
  return targetArray;
};

// ============================================
// ✅ GENERIC FUNCTION TO ADD TO DATABASE
// ============================================
const addToDatabase = async (type, value, targetArray) => {
  try {
    const normalizedValue = value.toUpperCase().trim();
    
    // ✅ Block adding 10/32 OZ
    if (type === 'pack_sizes' || type === 'pack_size') {
      const checkValue = normalizedValue.replace(/X/g, '/');
      if (BLOCKED_PACK_SIZES.some(blocked => 
        checkValue === blocked.toUpperCase().replace(/X/g, '/')
      )) {
        return { 
          success: false, 
          error: '10/32 OZ is not allowed. Please use a different pack size.'
        };
      }
    }
    
    console.log(`Adding ${type}:`, normalizedValue);
    
    const response = await axios.post(
      `${API_BASE_URL}/dropdowns.php?type=${type}`,
      { name: normalizedValue },
      {
        ...getAuthConfig(),
        headers: {
          ...getAuthConfig().headers,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log(`Add ${type} response:`, response.data);
    
    if (response.data && response.data.success) {
      const newValue = response.data.name || normalizedValue;
      if (!targetArray.includes(newValue)) {
        targetArray.push(newValue);
        targetArray.sort();
      }
      return { 
        success: true, 
        value: newValue,
        message: response.data.message,
        exists: response.data.exists || false
      };
    } else {
      return { 
        success: false, 
        error: response.data?.error || 'Unknown error from server'
      };
    }
  } catch (error) {
    console.error(`Failed to add ${type}:`, error);
    return { 
      success: false, 
      error: error.response?.data?.error || error.message || 'Network error'
    };
  }
};

// ============================================
// ✅ LOAD FUNCTIONS
// ============================================
export const loadProductsFromDatabase = () => loadFromDatabase('products', PRODUCTS);
export const loadGradesFromDatabase = () => loadFromDatabase('grades', GRADES);
export const loadBrandsFromDatabase = () => loadFromDatabase('brands', BRANDS);
export const loadPackSizesFromDatabase = () => loadFromDatabase('pack_sizes', PACK_SIZES);

// ============================================
// ✅ LOAD ALL DROPDOWN OPTIONS FROM DATABASE
// ============================================
export const loadDropdownOptions = async () => {
  try {
    const [products, grades, brands, packSizes] = await Promise.all([
      loadProductsFromDatabase(),
      loadGradesFromDatabase(),
      loadBrandsFromDatabase(),
      loadPackSizesFromDatabase()
    ]);
    
    return { 
      products,
      grades, 
      brands,
      pack_sizes: filterBlockedPackSizes(packSizes) // ✅ Extra filter just in case
    };
  } catch (error) {
    console.error('Failed to load dropdown options:', error);
    return { 
      products: PRODUCTS,
      grades: GRADES, 
      brands: BRANDS,
      pack_sizes: filterBlockedPackSizes(PACK_SIZES)
    };
  }
};

// ============================================
// ✅ ADD FUNCTIONS
// ============================================
export const addProductToDatabase = async (value) => {
  const result = await addToDatabase('products', value, PRODUCTS);
  return { ...result, product: result.value };
};

export const addGradeToDatabase = async (value) => {
  // Convert X to / for grades
  const normalized = value.replace(/[xX]/g, '/');
  const result = await addToDatabase('grades', normalized, GRADES);
  return { ...result, grade: result.value };
};

export const addBrandToDatabase = async (value) => {
  const result = await addToDatabase('brands', value, BRANDS);
  return { ...result, brand: result.value };
};

export const addPackSizeToDatabase = async (value) => {
  // Convert X to / for pack sizes
  const normalized = value.replace(/[xX]/g, '/');
  
  // ✅ Block 10/32 OZ
  if (BLOCKED_PACK_SIZES.some(blocked => 
    normalized.toUpperCase() === blocked.toUpperCase().replace(/X/g, '/')
  )) {
    return { 
      success: false, 
      error: '10/32 OZ is not allowed. Please use a different pack size.'
    };
  }
  
  const result = await addToDatabase('pack_sizes', normalized, PACK_SIZES);
  return { ...result, pack_size: result.value };
};

// ============================================
// ✅ HELPER: Format pack size for display (X → /)
// ============================================
export const formatPackSizeDisplay = (packSize) => {
  if (!packSize) return '';
  // ✅ Preserves decimals for display (e.g., 30/5.4 OZ)
  return String(packSize).replace(/[xX]/g, '/');
};

// ============================================
// ✅ HELPER: Format pack size for storage (X → /)
// ============================================
export const formatPackSizeStorage = (packSize) => {
  if (!packSize) return '';
  // ✅ Preserves decimals (e.g., 30/5.4 OZ, 12/2.5 LB, 16/1.75 LB)
  return String(packSize).replace(/[xX]/g, '/').toUpperCase().trim();
};

// ============================================
// ✅ HELPER: Check if pack size is blocked
// ============================================
export const isPackSizeBlocked = (packSize) => {
  if (!packSize) return false;
  const normalized = String(packSize).toUpperCase().trim().replace(/X/g, '/');
  return BLOCKED_PACK_SIZES.some(blocked => 
    normalized === blocked.toUpperCase().replace(/X/g, '/')
  );
};

// ============================================
// ✅ HELPER: Get filtered pack sizes (without blocked ones)
// ============================================
export const getFilteredPackSizes = () => {
  return filterBlockedPackSizes(PACK_SIZES);
};